const chapter = {
  id: 'limits-continuity-differentiability',
  slug: 'limits-continuity-differentiability',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Limits, Continuity & Differentiability',
        hindi: 'सीमाएँ, सातत्य और अवकलनीयता',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Medium-Hard',
        hindi: 'मध्यम-कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main, 1–2 in Advanced',
        hindi: 'जेईई मेन में 2-3 प्रश्न, एडवांस्ड में 1-2 प्रश्न',
  },
  syllabusTags: ['Calculus', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'limit-definition',
      title: {
        english: 'The Idea of a Limit',
        hindi: 'सीमा की अवधारणा',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Limit (informal)',
        hindi: 'सीमा (अनौपचारिक)',
          },
          body: {
            english: 'We say $\\displaystyle\\lim_{x \\to a} f(x) = L$ if values of $f(x)$ can be made **arbitrarily close** to $L$ by taking $x$ sufficiently close to $a$ (but $x \\ne a$). Existence of the limit does **not** require $f(a)$ to be defined.',
        hindi: 'हम $\\displaystyle\\lim_{x \\to a} f(x) = L$ कहते हैं यदि $f(x)$ के मानों को $x$ को $a$ (लेकिन $x \\ne a$) के पर्याप्त करीब ले जाकर $L$ के **मनमाने ढंग से करीब** बनाया जा सकता है। सीमा के अस्तित्व को परिभाषित करने के लिए $f(a)$ की **नहीं** आवश्यकता होती है।',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 380 240" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="200" x2="360" y2="200" stroke="#374151" stroke-width="1.2"/>
  <line x1="60" y1="20" x2="60" y2="220" stroke="#374151" stroke-width="1.2"/>
  <polyline points="70,175.4 74,173.7 78,172.1 82,170.6 86,169.2 90,167.8 94,166.5 98,165.3 102,164.1 106,162.9 110,161.8 114,160.7 118,159.7 122,158.7 126,157.7 130,156.8 134,155.9 138,155.0 142,154.2 146,153.3 150,152.5 154,151.7 158,151.0 162,150.2 166,149.5 170,148.8 174,148.1 178,147.4 182,146.7 186,146.1 190,145.4 194,144.8 198,144.2 202,143.6 206,143.0 210,142.4 214,141.9" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <polyline points="224,140.5 228,139.9 232,139.4 236,138.9 240,138.4 244,137.9 248,137.4 252,136.9 256,136.5 260,136.0 264,135.6 268,135.1 272,134.7 276,134.2 280,133.8 284,133.4 288,132.9 292,132.5 296,132.1 300,131.7 304,131.3 308,130.9 312,130.5 316,130.2 320,129.8 324,129.4 328,129.0 332,128.7 336,128.3 340,128.0 344,127.6 348,127.3 352,126.9 356,126.6" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <line x1="220" y1="200" x2="220" y2="90" stroke="#9ca3af" stroke-dasharray="4 3"/>
  <line x1="60" y1="90" x2="220" y2="90" stroke="#9ca3af" stroke-dasharray="4 3"/>
  <circle cx="220" cy="90" r="4" fill="#fff" stroke="#dc2626" stroke-width="2"/>
  <text x="216" y="214" font-size="12" fill="#1f2937">a</text>
  <text x="42" y="94" font-size="12" fill="#1f2937">L</text>
  <text x="190" y="225" font-size="12" fill="#6b7280" text-anchor="middle">lim_{x→a} f(x) = L  (value at a need not exist or equal L)</text>
</svg>`,
          caption: {
            english: 'Limit L exists at x = a even though the function value at a (open circle) may differ.',
        hindi: 'सीमा L x = a पर मौजूद है, भले ही a (खुले वृत्त) पर फ़ंक्शन मान भिन्न हो सकता है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'One-sided limits',
        hindi: 'एकतरफा सीमाएं',
          },
          statement: {
            english: 'Left-hand limit (LHL): $\\displaystyle\\lim_{x \\to a^-} f(x)$.  Right-hand limit (RHL): $\\displaystyle\\lim_{x \\to a^+} f(x)$. The two-sided limit exists iff **LHL = RHL** (and is finite).',
        hindi: 'बाएं हाथ की सीमा (एलएचएल): $\\displaystyle\\lim_{x \\to a^-} f(x)$। दाहिने हाथ की सीमा (आरएचएल): $\\displaystyle\\lim_{x \\to a^+} f(x)$। दोतरफा सीमा मौजूद है यदि **LHL = RHL** (और परिमित है)।',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'Substitute $x = a - h$ for LHL and $x = a + h$ for RHL with $h \\to 0^+$. This converts both into clean $h \\to 0^+$ limits.',
        hindi: 'LHL के लिए $x = a - h$ और RHL के लिए $x = a + h$ को $h \\to 0^+$ से बदलें। यह दोनों को स्वच्छ $h \\to 0^+$ सीमाओं में परिवर्तित कर देता है।',
          },
        },
      ],
    },
    {
      id: 'algebra',
      title: {
        english: 'Algebra of Limits',
        hindi: 'सीमाओं का बीजगणित',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Limit laws',
        hindi: 'सीमा कानून',
          },
          statement: {
            english: `If $\\lim_{x \\to a} f(x) = L$ and $\\lim_{x \\to a} g(x) = M$, then:
$\\bullet\\ \\lim (f \\pm g) = L \\pm M$
$\\bullet\\ \\lim (f \\cdot g) = LM$
$\\bullet\\ \\lim (f/g) = L/M$ (provided $M \\ne 0$)
$\\bullet\\ \\lim |f| = |L|$
$\\bullet\\ \\lim f^n = L^n$.`,
            hindi: 'यदि $\\lim_{x \\to a} f(x) = L$ और $\\lim_{x \\to a} g(x) = M$, तो: $\\bullet\\\\\\\\ \\lim (f \\pm g) = L \\pm M$ $\\bullet\\\\\\\\ \\lim (f \\cdot g) = LM$ $\\bullet\\\\\\\\ \\lim (f/g) = L/M$ ($M \\ne 0$ प्रदान किया गया) $\\bullet\\\\\\\\ \\lim |f| = |L|$ $\\bullet\\\\\\\\ \\lim f^n = L^n$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Sandwich (Squeeze) Theorem',
        hindi: 'सैंडविच (निचोड़) प्रमेय',
          },
          statement: {
            english: 'If $g(x) \\le f(x) \\le h(x)$ in a neighborhood of $a$ and $\\lim_{x \\to a} g(x) = \\lim_{x \\to a} h(x) = L$, then $\\lim_{x \\to a} f(x) = L$.',
        hindi: 'यदि $g(x) \\le f(x) \\le h(x)$ $a$ और $\\lim_{x \\to a} g(x) = \\lim_{x \\to a} h(x) = L$ के पड़ोस में है, तो $\\lim_{x \\to a} f(x) = L$।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Evaluate $\\displaystyle\\lim_{x \\to 0} x^2 \\sin(1/x)$.',
        hindi: '$\\displaystyle\\lim_{x \\to 0} x^2 \\sin(1/x)$ का मूल्यांकन करें। चूंकि $\\displaystyle\\lim_{x \\to 0} x^2 \\sin(1/x)$ और दोनों सीमाएं हैं, निचोड़ प्रमेय के अनुसार सीमा है।',
          },
          solution: {
            english: 'Since $-x^2 \\le x^2 \\sin(1/x) \\le x^2$ and both bounds $\\to 0$, by squeeze theorem the limit is $0$.',
        hindi: 'चूँकि $-x^2 \\le x^2 \\sin(1/x) \\le x^2$ और दोनों सीमाएँ $\\to 0$ हैं, निचोड़ प्रमेय के अनुसार सीमा $0$ है।',
          },
        },
      ],
    },
    {
      id: 'standard-limits',
      title: {
        english: 'Standard Limits',
        hindi: 'मानक सीमाएँ',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Limit', 'Value'],
          rows: [
            ['$\\displaystyle\\lim_{x \\to 0} \\dfrac{\\sin x}{x}$', '$1$'],
            ['$\\displaystyle\\lim_{x \\to 0} \\dfrac{\\tan x}{x}$', '$1$'],
            ['$\\displaystyle\\lim_{x \\to 0} \\dfrac{1 - \\cos x}{x^2}$', '$\\tfrac{1}{2}$'],
            ['$\\displaystyle\\lim_{x \\to 0} \\dfrac{\\sin^{-1} x}{x}$', '$1$'],
            ['$\\displaystyle\\lim_{x \\to 0} \\dfrac{\\tan^{-1} x}{x}$', '$1$'],
            ['$\\displaystyle\\lim_{x \\to 0} \\dfrac{e^x - 1}{x}$', '$1$'],
            ['$\\displaystyle\\lim_{x \\to 0} \\dfrac{a^x - 1}{x}$', '$\\ln a$'],
            ['$\\displaystyle\\lim_{x \\to 0} \\dfrac{\\ln(1+x)}{x}$', '$1$'],
            ['$\\displaystyle\\lim_{x \\to 0} (1 + x)^{1/x}$', '$e$'],
            ['$\\displaystyle\\lim_{x \\to \\infty} \\left(1 + \\dfrac{1}{x}\\right)^x$', '$e$'],
            ['$\\displaystyle\\lim_{x \\to a} \\dfrac{x^n - a^n}{x - a}$', '$n a^{n-1}$'],
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Always reduce expressions to one of these standard forms by algebraic manipulation, multiplication by conjugates, or substitution.',
        hindi: 'हमेशा बीजगणितीय हेरफेर, संयुग्मों द्वारा गुणन, या प्रतिस्थापन द्वारा अभिव्यक्ति को इन मानक रूपों में से एक में कम करें।',
          },
        },
      ],
    },
    {
      id: 'indeterminate',
      title: {
        english: 'Indeterminate Forms & L\'Hôpital\'s Rule',
        hindi: 'अनिश्चित फॉर्म और एल\\\'हॉपिटल का नियम',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'The seven indeterminate forms: $\\dfrac{0}{0},\\ \\dfrac{\\infty}{\\infty},\\ 0 \\cdot \\infty,\\ \\infty - \\infty,\\ 0^0,\\ \\infty^0,\\ 1^\\infty$.',
        hindi: 'सात अनिश्चित रूप हैं: $\\dfrac{0}{0},\\ \\dfrac{\\infty}{\\infty},\\ 0 \\cdot \\infty,\\ \\infty - \\infty,\\ 0^0,\\ \\infty^0,\\ 1^\\infty$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'L\'Hôpital\'s Rule',
        hindi: 'एल\\\'हॉपिटल का नियम',
          },
          statement: {
            english: `If $\\lim f(x)/g(x)$ is of form $0/0$ or $\\infty/\\infty$ and $f, g$ are differentiable near $a$ (with $g'(x) \\ne 0$), then
$$\\lim_{x \\to a} \\dfrac{f(x)}{g(x)} = \\lim_{x \\to a} \\dfrac{f'(x)}{g'(x)}$$
provided the right-hand limit exists.`,
            hindi: 'यदि $\\lim f(x)/g(x)$ $0/0$ या $\\infty/\\infty$ के रूप में है और $f, g$ $a$ ($g\\\'(x) \\ne 0$ के साथ) के पास भिन्न हैं, तो $$\\lim_{x \\to a} \\dfrac{f(x)}{g(x)} = \\lim_{x \\to a} \\dfrac{f\\\'(x)}{g\\\'(x)}$$ बशर्ते दाहिनी ओर की सीमा मौजूद हो।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: '$1^\\infty$ form',
        hindi: '$1^\\infty$ फॉर्म',
          },
          statement: {
            english: `If $\\lim_{x \\to a} f(x) = 1$ and $\\lim_{x \\to a} g(x) = \\infty$, then
$$\\lim_{x \\to a} f(x)^{g(x)} = e^{\\lim_{x \\to a} g(x)\\,(f(x) - 1)}.$$`,
            hindi: 'यदि $\\lim_{x \\to a} f(x) = 1$ और $\\lim_{x \\to a} g(x) = \\infty$, तो $$\\lim_{x \\to a} f(x)^{g(x)} = e^{\\lim_{x \\to a} g(x)\\,(f(x) - 1)}.$$',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Evaluate $\\displaystyle\\lim_{x \\to 0} \\dfrac{\\sin x - x}{x^3}$.',
        hindi: '$\\displaystyle\\lim_{x \\to 0} \\dfrac{\\sin x - x}{x^3}$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Form $0/0$. Apply L\'Hôpital thrice: $\\lim \\dfrac{\\cos x - 1}{3x^2} = \\lim \\dfrac{-\\sin x}{6x} = -\\dfrac{1}{6}$.',
        hindi: 'फॉर्म $0/0$. L\'Hôpital को तीन बार लागू करें: $\\lim \\dfrac{\\cos x - 1}',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Evaluate $\\displaystyle\\lim_{x \\to \\infty} \\left(\\dfrac{x+2}{x-1}\\right)^x$.',
        hindi: '$\\displaystyle\\lim_{x \\to \\infty} \\left(\\dfrac{x+2}{x-1}\\right)^x$ का मूल्यांकन करें.',
          },
          solution: {
            english: '$1^\\infty$ form. Take logs: $\\lim x \\ln \\dfrac{x+2}{x-1} = \\lim x \\cdot \\dfrac{3}{x-1} = 3$. Answer: $e^3$.',
        hindi: '$1^\\infty$ फॉर्म. लॉग लें: $\\lim x \\ln \\dfrac{x+2}{x-1} = \\lim x \\cdot \\dfrac{3}{x-1} = 3$. उत्तर: $e^3$.',
          },
        },
      ],
    },
    {
      id: 'continuity',
      title: {
        english: 'Continuity at a Point',
        hindi: 'एक बिन्दु पर सातत्य',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Continuity at $x = a$',
        hindi: '$x = a$ पर निरंतरता',
          },
          body: {
            english: `$f$ is continuous at $a$ iff three conditions hold:
1. $f(a)$ is defined,
2. $\\displaystyle\\lim_{x \\to a} f(x)$ exists (LHL = RHL = finite),
3. $\\displaystyle\\lim_{x \\to a} f(x) = f(a)$.`,
            hindi: '$f$ $a$ पर निरंतर है यदि तीन शर्तें लागू होती हैं: 1. $f(a)$ परिभाषित है, 2. $\\displaystyle\\lim_{x \\to a} f(x)$ मौजूद है (LHL = RHL = finite), 3. $\\displaystyle\\lim_{x \\to a} f(x) = f(a)$.',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 380 220" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="170" x2="360" y2="170" stroke="#374151" stroke-width="1.2"/>
  <line x1="60" y1="20" x2="60" y2="200" stroke="#374151" stroke-width="1.2"/>
  <!-- Continuous curve -->
  <path d="M 80 140 Q 140 60 200 110 T 340 80" fill="none" stroke="#10b981" stroke-width="2.2"/>
  <text x="220" y="50" font-size="12" fill="#047857">continuous: no jumps, no holes</text>
  <!-- Jump discontinuity demo -->
  <line x1="80" y1="160" x2="180" y2="160" stroke="#dc2626" stroke-width="2"/>
  <line x1="180" y1="120" x2="280" y2="120" stroke="#dc2626" stroke-width="2"/>
  <circle cx="180" cy="160" r="3.5" fill="#fff" stroke="#dc2626" stroke-width="2"/>
  <circle cx="180" cy="120" r="3.5" fill="#dc2626"/>
  <text x="190" y="190" font-size="11" fill="#b91c1c">jump discontinuity at x = c</text>
</svg>`,
          caption: {
            english: 'A continuous curve (green) versus a function with a jump discontinuity (red) at x = c.',
        hindi: 'एक सतत वक्र (हरा) बनाम x = c पर जंप डिसकंटीनिटी (लाल) वाला एक फ़ंक्शन। **हटाने योग्य असंततता**: x = c मौजूद है लेकिन (या अपरिभाषित)। **जंप डिसकंटिनिटी**: एलएचएल ≠ आरएचएल (दोनों परिमित)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Removable discontinuity**: $\\lim_{x \\to a} f(x)$ exists but $\\ne f(a)$ (or $f(a)$ undefined).',
        hindi: '**हटाने योग्य असंततता**: $\\lim_{x \\to a} f(x)$ मौजूद है लेकिन $\\ne f(a)$ (या $f(a)$ अपरिभाषित)।',
            },
            {
              english: '**Jump discontinuity**: LHL ≠ RHL (both finite).',
        hindi: '**कूद असंततता**: एलएचएल ≠ आरएचएल (दोनों परिमित)।',
            },
            {
              english: '**Infinite discontinuity**: at least one one-sided limit is $\\pm \\infty$.',
        hindi: '**अनंत असंततता**: कम से कम एक तरफा सीमा $\\pm \\infty$ है।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Algebra of continuous functions',
        hindi: 'सतत कार्यों का बीजगणित',
          },
          statement: {
            english: 'If $f, g$ are continuous at $a$, so are $f \\pm g,\\ fg,\\ f/g$ (if $g(a) \\ne 0$), and $f \\circ g$ (if $g$ continuous at $a$ and $f$ at $g(a)$).',
        hindi: 'यदि $f, g$ $a$ पर निरंतर हैं, तो $f \\pm g,\\ fg,\\ f/g$ (यदि $g(a) \\ne 0$), और $f \\circ g$ (यदि $g$ $a$ पर और $f$ $g(a)$ पर निरंतर हैं)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Intermediate Value Theorem (IVT)',
        hindi: 'मध्यवर्ती मूल्य प्रमेय (आईवीटी)',
          },
          statement: {
            english: 'If $f$ is continuous on $[a, b]$ and $k$ lies between $f(a)$ and $f(b)$, then there exists $c \\in (a, b)$ with $f(c) = k$. **Corollary**: if $f(a) f(b) < 0$, then $f$ has a root in $(a, b)$.',
        hindi: 'यदि $f$ $[a, b]$ पर निरंतर है और $k$ $f(a)$ और $f(b)$ के बीच स्थित है, तो $f(c) = k$ के साथ $c \\in (a, b)$ मौजूद है। **परिणाम**: यदि $f(a) f(b) < 0$, तो $f$ का मूल $(a, b)$ है।',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Find $a, b$ so that $f(x) = \\begin{cases} ax + 1 & x \\le 1 \\\\ x^2 + b & x > 1 \\end{cases}$ is continuous at $x = 1$.',
        hindi: '$a, b$ खोजें ताकि $f(x) = \\begin{cases} ax + 1 & x \\le 1 \\\\ x^2 + b & x > 1 \\end{cases}$ $x = 1$ पर निरंतर रहे।',
          },
          solution: {
            english: 'LHL $= a + 1 = f(1)$. RHL $= 1 + b$. Continuity ⇒ $a + 1 = 1 + b \\Rightarrow a = b$. (Any $a = b$ works.)',
        hindi: 'एलएचएल $= ए + 1 = एफ(1)$। आरएचएल $= 1 + बी$. निरंतरता ⇒ $a + 1 = 1 + b \\Rightarrow a = b$. (कोई भी $a = b$ काम करता है।)',
          },
        },
      ],
    },
    {
      id: 'differentiability',
      title: {
        english: 'Differentiability',
        hindi: 'अवकलनीयता',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Derivative at $x = a$',
        hindi: '$x = a$ पर व्युत्पन्न',
          },
          body: {
            english: `$f'(a) = \\displaystyle\\lim_{h \\to 0} \\dfrac{f(a + h) - f(a)}{h}$, provided this limit exists. **Left** and **right** derivatives:
$$f'(a^-) = \\lim_{h \\to 0^+} \\dfrac{f(a - h) - f(a)}{-h},\\quad f'(a^+) = \\lim_{h \\to 0^+} \\dfrac{f(a + h) - f(a)}{h}.$$
$f$ is differentiable at $a$ iff $f'(a^-) = f'(a^+)$ and the common value is finite.`,
            hindi: '$f\\\'(a) = \\displaystyle\\lim_{h \\to 0} \\dfrac{f(a + h) - f(a)}{h}$, बशर्ते यह सीमा मौजूद हो। **बाएँ** और **दाएँ** व्युत्पन्न: $$f\\\'(a^-) = \\lim_{h \\to 0^+} \\dfrac{f(a - h) - f(a)}{-h},\\quad f\\\'(a^+) = \\lim_{h \\to 0^+} \\dfrac{f(a + h) - f(a)}{h}.$$ $f$ $a$ पर अवकलनीय है यदि $f\\\'(a^-) = f\\\'(a^+)$ और सामान्य मान परिमित है।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Differentiable ⇒ Continuous',
        hindi: 'अवकलनीय ⇒ सतत',
          },
          statement: {
            english: 'If $f$ is differentiable at $a$, then $f$ is continuous at $a$. **Converse fails**: continuous functions need not be differentiable (e.g. $|x|$ at $0$).',
        hindi: 'यदि $f$ $a$ पर अवकलनीय है, तो $f$ $a$ पर सतत है। **बातचीत विफल**: निरंतर कार्यों को अलग-अलग करने की आवश्यकता नहीं है (उदाहरण के लिए $|x|$ पर $0$)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Geometric meaning**: $f\'(a)$ = slope of tangent to curve at $(a, f(a))$.',
        hindi: '**ज्यामितीय अर्थ**: $f\\\'(a)$ = $(a, f(a))$ पर वक्र की स्पर्शरेखा का ढलान।',
            },
            {
              english: '**Sharp corners** (kinks): function is continuous but not differentiable.',
        hindi: '**नुकीले कोने** (किंक): कार्य निरंतर है लेकिन अलग-अलग नहीं है।',
            },
            {
              english: '**Vertical tangent** (e.g. $x^{1/3}$ at $0$): derivative is $\\pm \\infty$ ⇒ not differentiable.',
        hindi: '**ऊर्ध्वाधर स्पर्शरेखा** (उदा. $x^{1/3}$ पर $0$): व्युत्पन्न $\\pm \\infty$ है ⇒ अवकलनीय नहीं है।',
            },
            {
              english: 'Functions involving $|x|$, $[x]$ (greatest integer), $\\{x\\}$ (fractional part) need careful piecewise checks.',
        hindi: '$|x|$, $[x]$ (सबसे बड़ा पूर्णांक), $\\{x\\}$ (आंशिक भाग) से जुड़े कार्यों को सावधानीपूर्वक टुकड़े-टुकड़े जांच की आवश्यकता होती है।',
            },
          ],
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Check differentiability of $f(x) = |x - 2|$ at $x = 2$.',
        hindi: '$x = 2$ पर $f(x) = |x - 2|$ की भिन्नता की जांच करें।',
          },
          solution: {
            english: '$f\'(2^-) = -1$, $f\'(2^+) = 1$. Not equal ⇒ **not differentiable** at 2 (though continuous).',
        hindi: '$f\\\'(2^-) = -1$, $f\\\'(2^+) = 1$। समान नहीं ⇒ **विभिन्न नहीं** 2 पर (यद्यपि निरंतर)।',
          },
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'For what value of $a$ is $f(x) = \\begin{cases} x^2 & x \\le 1 \\\\ ax + b & x > 1 \\end{cases}$ differentiable at $x = 1$ with $b = -1$?',
        hindi: '$a$ के किस मान के लिए $f(x) = \\begin{cases} x^2 & x \\le 1 \\\\ ax + b & x > 1 \\end{cases}$ $x = 1$ पर $b = -1$ से भिन्न है?',
          },
          solution: {
            english: 'Continuity: $1 = a + b \\Rightarrow a = 1 - b = 2$. Check derivatives: $f\'(1^-) = 2,\\ f\'(1^+) = a = 2$. ✓ So $a = 2$.',
        hindi: 'निरंतरता: $1 = ए + बी \\Rightarrow ए = 1 - बी = 2$। डेरिवेटिव की जाँच करें: $f\'(1^-) = 2,\\ f\'(1^+) = a = 2$। ✓ तो $a = 2$.',
          },
        },
      ],
    },
    {
      id: 'special-functions',
      title: {
        english: 'Continuity & Differentiability of Special Functions',
        hindi: 'विशेष फलनों का सातत्य और अवकलनीयता',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Function', 'Continuous on', 'Differentiable on'],
          rows: [
            ['Polynomial $p(x)$', '$\\mathbb{R}$', '$\\mathbb{R}$'],
            ['$\\sin x,\\ \\cos x$', '$\\mathbb{R}$', '$\\mathbb{R}$'],
            ['$\\tan x$', '$\\mathbb{R} \\setminus \\{(2k+1)\\pi/2\\}$', 'same'],
            ['$|x|$', '$\\mathbb{R}$', '$\\mathbb{R} \\setminus \\{0\\}$'],
            ['$[x]$ (greatest integer)', '$\\mathbb{R} \\setminus \\mathbb{Z}$', '$\\mathbb{R} \\setminus \\mathbb{Z}$'],
            ['$\\{x\\}$ (fractional part)', '$\\mathbb{R} \\setminus \\mathbb{Z}$', '$\\mathbb{R} \\setminus \\mathbb{Z}$'],
            ['$e^x,\\ \\ln x$ (on domain)', 'domain', 'domain'],
            ['$\\sqrt x$', '$[0, \\infty)$', '$(0, \\infty)$ — vertical tangent at 0'],
          ],
        },
        {
          type: 'note',
          text: {
            english: 'In JEE problems, points where differentiability is suspect are typically (i) where modulus opens, (ii) integer points for $[x], \\{x\\}$, (iii) piecewise junctions, (iv) where denominator vanishes.',
        hindi: 'जेईई समस्याओं में, वे बिंदु जहां भिन्नता पर संदेह होता है, वे आम तौर पर होते हैं (i) जहां मापांक खुलता है, (ii) $[x], \\{x\\}$ के लिए पूर्णांक बिंदु, (iii) टुकड़े-टुकड़े जंक्शन, (iv) जहां हर गायब हो जाता है।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\lim_{x \\to 0} \\tfrac{\\sin x}{x} = 1',
      desc: {
        english: 'Trig standard limit',
        hindi: 'ट्रिग मानक सीमा',
      },
    },
    {
      latex: '\\lim_{x \\to 0} \\tfrac{1 - \\cos x}{x^2} = \\tfrac{1}{2}',
      desc: {
        english: '',
        hindi: 'एक्स 0 एक्स 2 = 2',
      },
    },
    {
      latex: '\\lim_{x \\to 0} \\tfrac{e^x - 1}{x} = 1',
      desc: {
        english: '',
        hindi: 'एक्स 0 एक्स = 1',
      },
    },
    {
      latex: '\\lim_{x \\to 0} \\tfrac{\\ln(1+x)}{x} = 1',
      desc: {
        english: '',
        hindi: 'एक्स 0 एक्स = 1',
      },
    },
    {
      latex: '\\lim_{x \\to 0} (1+x)^{1/x} = e',
      desc: {
        english: 'Euler limit',
        hindi: 'यूलर सीमा',
      },
    },
    {
      latex: '1^\\infty:\\ e^{\\lim g(f-1)}',
      desc: {
        english: 'Resolution of 1^∞ form',
        hindi: '1^∞ फॉर्म का रिज़ॉल्यूशन',
      },
    },
    {
      latex: 'f\'(a) = \\lim_{h \\to 0} \\tfrac{f(a+h)-f(a)}{h}',
      desc: {
        english: 'Definition of derivative',
        hindi: 'व्युत्पन्न की परिभाषा',
      },
    },
    {
      latex: 'f\\ \\text{diff at}\\ a \\Rightarrow f\\ \\text{cts at}\\ a',
      desc: {
        english: 'One-way implication',
        hindi: 'एकतरफा निहितार्थ',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Standard limit',
      problem: {
        english: 'Evaluate $\\displaystyle\\lim_{x \\to 0} \\dfrac{\\sin 3x \\cdot \\tan 5x}{x^2}$.',
        hindi: '$\\displaystyle\\lim_{x \\to 0} \\dfrac{\\sin 3x \\cdot \\tan 5x}{x^2}$ का मूल्यांकन करें.',
      },
      answer: {
        english: '$15$',
        hindi: '$15$',
      },
      solution: {
        english: '$\\lim \\dfrac{\\sin 3x}{3x} \\cdot \\dfrac{\\tan 5x}{5x} \\cdot 15 = 1 \\cdot 1 \\cdot 15 = 15$.',
        hindi: '$\\lim \\dfrac{\\sin 3x}{3x} \\cdot \\dfrac{\\tan 5x}{5x} \\cdot 15 = 1 \\cdot 1 \\cdot 15 = 15$.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: '1^∞',
      problem: {
        english: '$\\displaystyle\\lim_{x \\to 0} (\\cos x)^{1/x^2}$.',
        hindi: '$\\displaystyle\\lim_{x \\to 0} (\\cos x)^{1/x^2}$.',
      },
      answer: {
        english: '$e^{-1/2}$',
        hindi: '$e^{-1/2}$',
      },
      solution: {
        english: '$1^\\infty$ form: $\\exp\\left(\\lim \\dfrac{\\cos x - 1}{x^2}\\right) = e^{-1/2}$.',
        hindi: '$1^\\infty$ फॉर्म: $\\exp\\left(\\lim \\dfrac{\\cos x - 1}{x^2}\\right) = e^{-1/2}$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Continuity',
      problem: {
        english: 'If $f(x) = \\dfrac{\\sin(\\pi x)}{x}$ for $x \\ne 0$ is continuous at $0$, then $f(0) = ?$',
        hindi: 'यदि $x \\ne 0$ के लिए $f(x) = \\dfrac{\\sin(\\pi x)}{x}$ $0$ पर निरंतर है, तो $f(0) =?$',
      },
      answer: {
        english: '$\\pi$',
        hindi: '$\\pi$',
      },
      solution: {
        english: '$\\lim_{x \\to 0} \\dfrac{\\sin \\pi x}{x} = \\pi \\cdot \\lim \\dfrac{\\sin \\pi x}{\\pi x} = \\pi$.',
        hindi: '$\\lim_{x \\to 0} \\dfrac{\\sin \\pi x}{x} = \\pi \\cdot \\lim \\dfrac{\\sin \\pi x}{\\pi x} = \\pi$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Differentiability',
      problem: {
        english: 'Number of points in $[0, 4]$ where $f(x) = |x - 1| + |x - 2| + |x - 3|$ is **not** differentiable:',
        hindi: '$[0, 4]$ में अंकों की संख्या जहां $f(x) = |x - 1| + |x - 2| + |x - 3|$ **नहीं** भिन्न है:',
      },
      answer: {
        english: '$3$',
        hindi: '$3$',
      },
      solution: {
        english: 'Non-differentiable at each kink: $x = 1, 2, 3$.',
        hindi: 'प्रत्येक किंक पर गैर-विभेदित: $x = 1, 2, 3$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'L\'Hôpital',
      problem: {
        english: 'Evaluate $\\displaystyle\\lim_{x \\to 0} \\dfrac{x \\cos x - \\sin x}{x^3}$.',
        hindi: '$\\displaystyle\\lim_{x \\to 0} \\dfrac{x \\cos x - \\sin x}{x^3}$ का मूल्यांकन करें.',
      },
      answer: {
        english: '$-\\tfrac{1}{3}$',
        hindi: '$-\\tfrac{1}{3}$',
      },
      solution: {
        english: 'L\'Hôpital: numerator derivative $= \\cos x - x\\sin x - \\cos x = -x\\sin x$. So limit $= \\lim \\dfrac{-x \\sin x}{3x^2} = -\\dfrac{1}{3}$.',
        hindi: 'एल\'हॉपिटल: अंश व्युत्पन्न $= \\cos x - x\\sin x - \\cos x = -x\\sin x$। तो सीमा $= \\lim \\dfrac{-x \\sin x}{3x^2} = -\\dfrac{1}{3}$।',
      },
    },
  ],
  next: {
    slug: 'differentiation',
    title: 'Differentiation',
  },
};

export default chapter;
